/**
 * Parse HTML wpisu WP i wyciagnij metryki strukturalne.
 * Bezstanowy, czyste funkcje. Uzywa browser DOMParser.
 *
 * Output: WpHtmlMetrics (zdefiniowane w types/thematicMaps.ts)
 */

import type { WpHtmlMetrics } from "@/types/thematicMaps";
import { POLISH_STOPWORDS_SET, stripPolishDiacritics } from "@/utils/stopwords";

const EMPTY_METRICS: WpHtmlMetrics = {
  word_count: 0,
  char_count: 0,
  h1_count: 0,
  h2_count: 0,
  h3_count: 0,
  h4_count: 0,
  paragraph_count: 0,
  list_count: 0,
  image_count: 0,
  outbound_links: [],
  internal_links: [],
  plaintext: "",
  first_paragraph: "",
  keyword_stuffing_score: 100,
  top_words: [],
};

function hostOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

function normalizeDomain(siteDomain: string): string {
  // siteDomain moze byc "example.com" lub "https://example.com" lub pelny URL
  const asHost = hostOf(siteDomain);
  if (asHost) return asHost;
  return siteDomain.toLowerCase().replace(/^www\./, "").replace(/\/$/, "");
}

function classifyLink(href: string, siteHost: string): "outbound" | "internal" | "anchor" {
  if (!href) return "anchor";
  if (href.startsWith("#")) return "anchor";
  if (href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("javascript:")) {
    return "anchor";
  }
  // Relative
  if (!/^https?:\/\//i.test(href)) return "internal";
  // Absolute — porownaj host
  const host = hostOf(href);
  if (!host) return "anchor";
  return host === siteHost ? "internal" : "outbound";
}

/**
 * Analyze WordPress post HTML.
 * @param html rendered HTML (z content.rendered)
 * @param siteDomain domena wpisu (dla rozroznienia outbound vs internal)
 */
export function analyzeWpHtml(html: string, siteDomain: string): WpHtmlMetrics {
  if (!html || typeof html !== "string" || html.trim().length === 0) {
    return { ...EMPTY_METRICS };
  }

  const siteHost = normalizeDomain(siteDomain);

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return { ...EMPTY_METRICS };
  }

  const body = doc.body;
  if (!body) return { ...EMPTY_METRICS };

  // Usun script/style zeby nie zaliczaly sie do tekstu/ slow
  body.querySelectorAll("script, style, noscript").forEach((el) => el.remove());

  // Pobierz plaintext
  const plaintext = (body.textContent || "").replace(/\s+/g, " ").trim();

  // Word / char count
  const tokens = plaintext.split(/\s+/).filter((t) => t.length > 0);
  const word_count = tokens.length;
  const char_count = plaintext.length;

  // Headings
  const h1_count = body.querySelectorAll("h1").length;
  const h2_count = body.querySelectorAll("h2").length;
  const h3_count = body.querySelectorAll("h3").length;
  const h4_count = body.querySelectorAll("h4").length;

  // Paragraphs / lists / images
  const paragraph_count = body.querySelectorAll("p").length;
  const list_count = body.querySelectorAll("ul, ol").length;
  const image_count = body.querySelectorAll("img").length;

  // Links
  const outbound_links: string[] = [];
  const internal_links: string[] = [];
  body.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href") || "";
    const kind = classifyLink(href, siteHost);
    if (kind === "outbound") outbound_links.push(href);
    else if (kind === "internal") internal_links.push(href);
  });

  // First paragraph
  const firstP = body.querySelector("p");
  const first_paragraph = firstP ? (firstP.textContent || "").trim() : "";

  // Keyword stuffing analysis — zlicz czestotliwosc non-stopword tokens
  const normalized = stripPolishDiacritics(plaintext.toLowerCase());
  const nonStopTokens = normalized.split(/\W+/).filter((t) => t.length > 2 && !POLISH_STOPWORDS_SET.has(t));
  const totalNonStop = nonStopTokens.length;

  const freqMap = new Map<string, number>();
  for (const tok of nonStopTokens) {
    freqMap.set(tok, (freqMap.get(tok) || 0) + 1);
  }

  const freqArr = Array.from(freqMap.entries())
    .map(([word, frequency]) => ({
      word,
      frequency,
      density_pct: totalNonStop > 0 ? (frequency / totalNonStop) * 100 : 0,
    }))
    .sort((a, b) => b.frequency - a.frequency);

  const top_words = freqArr.slice(0, 10);

  // Stuffing score:
  // sum(top_3 density) — typowo natural 3-7%, podejrzane 10-15%, stuffed >15%
  const top3Density = top_words.slice(0, 3).reduce((s, w) => s + w.density_pct, 0);
  // score = 100 - clamp(top3Density * 5, 0, 100)
  // density 3% → score 85, 8% → 60, 15% → 25
  const keyword_stuffing_score = Math.max(0, Math.min(100, Math.round(100 - top3Density * 5)));

  return {
    word_count,
    char_count,
    h1_count,
    h2_count,
    h3_count,
    h4_count,
    paragraph_count,
    list_count,
    image_count,
    outbound_links,
    internal_links,
    plaintext,
    first_paragraph,
    keyword_stuffing_score,
    top_words,
  };
}
